import type { SwitchProps as MuiSwitchProps } from '@mui/material/Switch';

import MuiFormControlLabel from '@mui/material/FormControlLabel';
import Skeleton from '@mui/material/Skeleton';
import MuiSwitch from '@mui/material/Switch';
import classNames from 'classnames';
import React from 'react';

import type { FieldProps } from '../types';

import { useIntersectionObserver } from '../../../hooks';
import { HelperMessage } from '../../HelperMessage';
import { ErrorMessage } from '../../ErrorMessage';
import './SwitchField.scss';

export type SwitchFieldProps = {
  onChange?:
    | ((
        event: React.ChangeEvent<HTMLInputElement>,
        value: { checked: boolean; value: string },
      ) => void)
    | undefined;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onIntersection?: () => void;
  defaultValue?: string;
  isObserved?: boolean;
  // onChange
} & Pick<MuiSwitchProps, 'checked' | 'classes' | 'defaultChecked' | 'value'> &
  FieldProps;

export const SwitchField = React.forwardRef(
  (
    {
      checked,
      className,
      classes,
      'data-testid': testId,
      defaultChecked,
      disabled,
      errors,
      helperText,
      isObserved,
      label,
      loading,
      name,
      onChange,
      onClick,
      onIntersection,
      value,
    }: SwitchFieldProps,
    ref: React.ForwardedRef<HTMLButtonElement>,
  ) => {
    const handleChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        onChange?.(event, { checked, value });
      },
      [onChange, value],
    );
    const containerRef = React.useRef<HTMLDivElement>(null);

    useIntersectionObserver({
      isObserved,
      onIntersection,
      ref: containerRef,
    });

    if (loading) {
      return (
        <Skeleton
          className={classNames(
            'px-4 flex flex-col Mfw-SwitchField-root--loading',
            className,
          )}
          height={42}
        />
      );
    }
    return (
      <div
        className={classNames(
          'px-4 flex flex-col Mfw-SwitchField-root',
          className,
        )}
        ref={containerRef}
      >
        <MuiFormControlLabel
          control={
            <MuiSwitch
              defaultChecked={defaultChecked}
              onChange={handleChange}
              defaultValue={value}
              data-testid={testId}
              disabled={disabled}
              onClick={onClick}
              checked={checked}
              classes={classes}
              value={value}
              name={name}
              ref={ref}
            />
          }
          className="Mfw-SwitchField-formControlLabel"
          disabled={disabled}
          label={label}
        />
        {helperText && (
          <HelperMessage className="Mfw-SwitchField-helperText">
            {helperText}
          </HelperMessage>
        )}
        {errors && (
          <ErrorMessage
            className="Mfw-SwitchField-errorMessage"
            errors={errors}
          />
        )}
      </div>
    );
  },
);

SwitchField.displayName = 'SwitchField';

export default SwitchField;
